import { defineComponent as _defineComponent } from 'vue'
import {ref, reactive, computed, defineAsyncComponent, onMounted, Ref} from "vue";
import { useTranslations } from "@/hooks/useTranslations";
import VBreadcrumbs from "@/components/vBreadcrumbs.vue";
import UserDetails from "@/models/User/UserDetails";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import VModal from "@/components/vModal.vue";
import VTable from "@/components/vTable.vue";
import VSelectFx from "@/components/vSelectFx.vue";
import AdditionalCostRepository from "@/utils/API/AdditionalCostRepository";
import {AdditionalCost} from "@/models/AdditionalCost/AdditionalCost";
import {useStoreState} from "@/hooks/useStoreState";
import {useStore} from "@/hooks/useStore";
import {MeasurementCategory} from "@/models/Measurement/MeasurementCategory";
import {IngredientCostMeasurement} from "@/models/IngredientCost/IngredientCostMeasurement";
import {AxiosResponse} from "axios";
import {ListResult} from "@/utils/API/ResultTypes/ListResult";
import {Measurement} from "@/models/Measurement/Measurement";
import MeasurementRepository from "@/utils/API/MeasurementRepository";
import VImportBulkCostsModal from "@/components/costing/vImportBulkCostsModal.vue";
import VModifyAdditionalCost from "@/components/costing/vModifyAdditionalCost.vue";
import { dateTimeFormatter} from "@/utils/Helpers";
import VAddBulkCostModal from "@/components/costing/VAddBulkCostModal.vue";
import {ModifyIngredientCostDto} from "@/models/RecipeInvoice/ModifyIngredientCostDto";


export default /*#__PURE__*/_defineComponent({
  __name: 'AdditionalCost',
  setup(__props) {

let translations = ref(useTranslations());
const vSelectFx = defineAsyncComponent(() => import("@/components/vSelectFx.vue"));

const storeState = useStoreState();
const store = useStore();

const localAdditionalCosts = ref<AdditionalCost[]>([]);
const tempAdditionalCostsFromFile = ref<ModifyIngredientCostDto[]>([]);
const totalCount = ref(0);
const count = ref(0);
const searchTerm = ref("");
const selectedAdditionalCost: Ref<AdditionalCost> = ref(null);
const massMeasurements = ref<IngredientCostMeasurement[]>([]);

let showModifyAdditionalCostModal = ref(false);
let showImportCostModal = ref(false);
let showCostAddingModal = ref(false);

const loading = ref(false);
const deletingModal = reactive({
  isOpen: false,
  ids: [] as number[],
});

const tableHeaders = computed(() => [
  { name: translate("cost_additional_table_name"), key: "Name", searchable: false, sortable: true },
  { name: translate("ingr-cost-table-vendor"), key: "Supplier", searchable: false, sortable: true },
  { name: translate("ingr-cost-table-quantity"), key: "Quantity", searchable: false, sortable: false },
  { name: translate("ingr-cost-table-measurement"), key: "Measurement", searchable: false, sortable: false },
  { name: translate("ingr-cost-table-cost"), key: "Cost", searchable: false, sortable: false },
  { name: translate("ingr-cost-table-date"), key: "CreatedOn", searchable: false, searchString: "", sortable: true,},
  { name: translate("ingr-cost-table-updated"), key: "UpdatedOn", searchable: false, searchString: "", sortable: true,},
]);

const userDetails: UserDetails = null;
const hasAdditionalCosts = computed(() => localAdditionalCosts.value.length > 0);

const filteredAdditionalCosts = computed(() =>
  localAdditionalCosts.value.filter((x) =>
    x.name.toLowerCase().includes(searchTerm.value.toLowerCase())
  )
);

const additionalCostsForGrid = computed(() =>
  filteredAdditionalCosts.value.map((x, index) => {
    return {
      meta: {id: x.id, link: x.name},
      e1: x.vendor.name,
      e2: x.quantity,
      e3: x.ingredientCostMeasurement.measurement.name,
      e4: (Math.round(x.cost * 100) / 100).toFixed(2),
      e5: dateTimeFormatter(x.createdOn),
      e6: dateTimeFormatter(x.updatedOn),
    };
  })
);

function refreshAdditionalCosts() {
  fetchAdditionalCosts();
}

function editAdditionalCost(itemId: number) {
  selectedAdditionalCost.value = localAdditionalCosts.value.find((x) => x.id === itemId) || null;
  showModifyAdditionalCostModal.value = true;
}

function hideEditAdditionalCostModal(){
  showModifyAdditionalCostModal.value = false;
}

function hideImportCostModal(){
  showImportCostModal.value = false;
}

function handleImportedIngredients(importedIngredients: ModifyIngredientCostDto[]) {
  tempAdditionalCostsFromFile.value = importedIngredients.map(toCreateDto);
  showCostAddingModal.value = true;
}

function toCreateDto(cost: ModifyIngredientCostDto) {
  return {
    name: cost.name,
    vendorId: cost.vendorId,
    cost: cost.cost,
    quantity: cost.quantity,
    chosenMeasurementName: cost.chosenMeasurementName,
    ingredientCostMeasurements: [...massMeasurements.value]
  };
}

function handleImportedIngredientsSave(){
  showCostAddingModal.value = false;
  fetchAdditionalCosts();
}

async function removeAdditionalCost(ids: number[]) {
  loading.value = true;
  try {
    await AdditionalCostRepository.deleteAdditionalCosts(ids);
    localAdditionalCosts.value = localAdditionalCosts.value.filter((c) => !ids.includes(c.id));
  }
  catch (error) {
    console.error("Failed to fetch additional costs", error);
  }
  finally {
    loading.value = false;
  }
}

async function fetchAdditionalCosts() {
  loading.value = true;
  try {
    const response = await AdditionalCostRepository.getAdditionalCosts(storeState.value.AccountModule.userDetails.restaurantId);
    localAdditionalCosts.value = response.data;
  }
  catch (error) {
    console.error("Failed to fetch additional costs", error);
  }
  finally {
    loading.value = false;
  }
}

function exportFile() {
  return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes/AdditionalCost/ExportAdditionalCosts`;
}

function translate(key: string) {
  return translations.value.get(key);
}

function openAddAdditionalCostModal() {
  // @ts-expect-error type is close enough
  selectedAdditionalCost.value = {
    id: 0,
    name: "",
    cost: 0,
    restaurantId: 0,
    recipeInvoiceId: 0,
    vendor: {
      name: "",
      restaurantId: 0,
      createdOn: null,
      id: 0
    },
    quantity: 0,
    ingredientCostMeasurementId: 0,
    vendorId: 0,
    updatedOn: null,
    createdOn: null,
    ingredientCostMeasurement: {
      measurement: {
        conversionCoeficient: 0,
        isDefault: false,
        measurementCategory: MeasurementCategory.Mass,
        name: "",
        id: 0,
      },
      weightInGrams: 0,
      measurementId: 0,
      ingredientCostId: 0,
      createdOn: null,
      id: 0
    },
    ingredientCostMeasurements: massMeasurements.value,
  };
  showModifyAdditionalCostModal.value = true;
}

function openImportAdditionalCostModal() {
  showImportCostModal.value = true;
}

function submitDelete() {}

function downloadImportTemplate() {
  return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes/AdditionalCost/DownloadAdditionalCostImportTemplate`;
}

async function loadMeasurements() {
  let massRes: AxiosResponse<ListResult<Measurement>>;
  try {
    massRes = await MeasurementRepository.listMeasurementsForCategory(
      MeasurementCategory.Mass
    );
  }

  catch (err) {
    console.error("Failed to load measurements", err);
  }

  const mass = massRes.data.result.map((x) => ({
    id: x.id,
    weightInGrams: x.conversionCoeficient,
    ingredientCostId: 0,
    createOn: null,
    measurementId: 0,
    measurement: x,
  }));

  const volRes = await MeasurementRepository.listMeasurementsForCategory(
    MeasurementCategory.Volume
  );

  const volume = volRes.data.result
    .filter((v) => !massRes.data.result.some((m) => m.id === v.id))
    .map((x) => ({
      id: x.id,
      weightInGrams: x.conversionCoeficient,
      createOn: null,
      ingredientCostId: 0,
      measurementId: 0,
      measurement: x,
    }));

  massMeasurements.value = mass.concat(volume);
}

onMounted(async() => {
  try {
    await store.value.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["common", "recipe costing", "ingredients", "ingredient cost"],
    });
    document.title =
      translate("additional_cost") + " " + translate("meta_title_postfix");
  }
  catch (error: any) {
  }
  await fetchAdditionalCosts();
  await loadMeasurements();
});


return { __sfc: true,translations, vSelectFx, storeState, store, localAdditionalCosts, tempAdditionalCostsFromFile, totalCount, count, searchTerm, selectedAdditionalCost, massMeasurements, showModifyAdditionalCostModal, showImportCostModal, showCostAddingModal, loading, deletingModal, tableHeaders, userDetails, hasAdditionalCosts, filteredAdditionalCosts, additionalCostsForGrid, refreshAdditionalCosts, editAdditionalCost, hideEditAdditionalCostModal, hideImportCostModal, handleImportedIngredients, toCreateDto, handleImportedIngredientsSave, removeAdditionalCost, fetchAdditionalCosts, exportFile, translate, openAddAdditionalCostModal, openImportAdditionalCostModal, submitDelete, downloadImportTemplate, loadMeasurements, VBreadcrumbs, VModal, VTable, VSelectFx, VImportBulkCostsModal, VModifyAdditionalCost, VAddBulkCostModal }
}

})